import { Resource } from '@nextforce/api'
import { makeStyles, Text } from '@rneui/themed'
import { t } from 'app/i18l'
import {
  ChatGroupDocumentGridItemRow,
  ROW_ITEMS,
} from 'file_folder_repository/views/grid_item'
import moment from 'moment'
import { useMemo } from 'react'
import { SectionList, View } from 'react-native'
import Animated, { FadeIn } from 'react-native-reanimated'
import { getDarkShadeColor, getLightShadeColor } from 'shared/utils'

interface Props {
  chatGroupFileFolderList: Resource[] | undefined
  onPress(resource: Resource): void
  onLongPress(resource: Resource): void
}

interface Section {
  id: number
  title: string
  data: Resource[]
}

export function FileFolderGridView({
  chatGroupFileFolderList,
  onPress,
  onLongPress,
}: Props) {
  const styles = useStyles()
  const sections: Section[] = useMemo(
    () => groupFileFoldersByDate(chatGroupFileFolderList),
    [chatGroupFileFolderList]
  )

  const rowMap = useMemo(() => {
    const map = new Map<number, Map<number, string[]>>() // sectionId -> rowStartIndex -> itemIds

    sections.forEach(section => {
      const sectionRowMap = new Map<number, string[]>()
      let currentRowStartIndex = 0
      let currentRow: string[] = []
      let spaceRemaining = ROW_ITEMS

      section.data.forEach((item, index) => {
        const itemSpace = item.type === 'folder' ? 2 : 1

        if (spaceRemaining >= itemSpace) {
          currentRow.push(item.id)
          spaceRemaining -= itemSpace

          if (index === section.data.length - 1 || spaceRemaining === 0) {
            sectionRowMap.set(currentRowStartIndex, currentRow)
            currentRow = []
            currentRowStartIndex = index + 1
            spaceRemaining = ROW_ITEMS
          }
        } else {
          sectionRowMap.set(currentRowStartIndex, currentRow)
          currentRow = [item.id]
          spaceRemaining = ROW_ITEMS - itemSpace
          currentRowStartIndex = index
        }
      })

      if (currentRow.length > 0) {
        sectionRowMap.set(currentRowStartIndex, currentRow)
      }

      map.set(section.id, sectionRowMap)
    })

    return map
  }, [sections])

  return (
    <View style={styles.container}>
      <SectionList
        sections={sections}
        stickySectionHeadersEnabled={true}
        renderItem={({ item: resource, index, section }) => {
          const sectionRowMap = rowMap.get(section.id)

          if (sectionRowMap && sectionRowMap.has(index)) {
            const rowItemIds = sectionRowMap.get(index)!
            const rowItems = section.data.filter(item =>
              rowItemIds.includes(item.id)
            )

            return (
              <Animated.View entering={FadeIn.delay(index * 50)}>
                <ChatGroupDocumentGridItemRow
                  items={rowItems}
                  onPress={onPress}
                  onLongPress={onLongPress}
                />
              </Animated.View>
            )
          }

          return null
        }}
        renderSectionHeader={({ section }) => (
          <Text style={styles.sectionHeader}>{section.title}</Text>
        )}
        keyExtractor={(item, index) => item.id + index}
        contentContainerStyle={styles.listContentContainer}
        ListEmptyComponent={() => (
          <View style={styles.emptyComponent}>
            <Text style={styles.emptyComponentText}>
              {t('noFilesOrFoldersFound')}
            </Text>
          </View>
        )}
      />
    </View>
  )
}

function groupFileFoldersByDate(resources: Resource[] | undefined): Section[] {
  const groupedFileFolders: { [key: string]: Resource[] } = {}

  resources?.forEach(resource => {
    const resourceDate = moment(resource.created)
    let dateKey = ''

    if (resourceDate.isSame(moment(), 'day')) {
      dateKey = 'today'
    } else if (resourceDate.isSame(moment(), 'week')) {
      dateKey = 'thisWeek'
    } else if (resourceDate.isSame(moment().subtract(1, 'week'), 'week')) {
      dateKey = 'lastWeek'
    } else if (resourceDate.isSame(moment(), 'month')) {
      dateKey = 'thisMonth'
    } else if (resourceDate.isSame(moment().subtract(1, 'month'), 'month')) {
      dateKey = 'lastMonth'
    } else {
      dateKey = resourceDate.format('MMMM YYYY')
    }

    // Ensure that items can only be added to one category
    if (!groupedFileFolders[dateKey]) {
      groupedFileFolders[dateKey] = []
    }

    // Prevent duplicates in the same section
    if (
      !groupedFileFolders[dateKey].some(
        existingResource => existingResource.id === resource.id
      )
    ) {
      groupedFileFolders[dateKey].push(resource)
    }
  })

  // Sort the keys
  const sortedKeys = Object.keys(groupedFileFolders).sort((a, b) => {
    const order = ['today', 'thisWeek', 'lastWeek', 'thisMonth', 'lastMonth']
    const aIndex = order.indexOf(a)
    const bIndex = order.indexOf(b)

    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex
    if (aIndex !== -1) return -1
    if (bIndex !== -1) return 1

    return moment(b, 'MMMM YYYY').isBefore(moment(a, 'MMMM YYYY')) ? 1 : -1
  })

  // Create sections with titles
  const sections: Section[] = sortedKeys.map((sortedKey, index) => ({
    id: index,
    data: groupedFileFolders[sortedKey],
    title: ['today', 'thisWeek', 'lastWeek', 'thisMonth', 'lastMonth'].includes(
      sortedKey
    )
      ? t(sortedKey)
      : sortedKey,
  }))

  return sections
}

const useStyles = makeStyles(theme => ({
  container: {
    flex: 1,
    paddingHorizontal: 0,
  },
  sectionHeader: {
    fontSize: 18,
    fontWeight: 'bold',
    paddingVertical: 8,
    paddingHorizontal: 16,
    backgroundColor:
      theme.mode === 'dark' ? getDarkShadeColor(0.7) : getLightShadeColor(0.95),
  },
  chatGroupMediaListContainer: { paddingBottom: 100 },
  filtersRowContainer: { paddingHorizontal: 10 },
  emptyComponent: { flex: 1, justifyContent: 'center' },
  emptyComponentText: { textAlign: 'center' },
  listContentContainer: {
    paddingBottom: 100,
    flexGrow: 1,
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.white : theme.colors.grey6,
  },
}))
